import React, { useState } from 'react';
import { AlertCircle, Car, Info, Mail, Phone, User, X } from 'lucide-react';
import useEscapeToClose from './useEscapeToClose';

/**
 * PickupConfirmModal
 *
 * The popup that fires when staff click "Vehicle Received" on a booking — the moment the
 * customer is standing at the counter with their keys.
 *
 * It has two jobs. The first is confirming who is collecting the car afterwards. The
 * second is <strong>collecting what the phone bot could not</strong>: the AI voice line
 * takes a booking with only the number the carrier supplied, because a name and an email
 * spoken down a phone are unreliable — 28 real calls produced 8 usable names — and losing
 * a booking to collect either is the wrong trade.
 *
 * The backend REFUSES the vehicle-received transition while either is missing
 * (HandoverDetailCapture), so those fields are mandatory when shown. Blocking here gives
 * staff a field to fill instead of an error with no way forward. A customer who already
 * has both never sees them.
 *
 * <p>Extracted from BookingsPage.jsx, where it sat inside a 3,469-line file with no tests
 * — the same reason the backend rule it serves had none. On its own it can be specced.
 *
 * Props:
 *   booking   — the selected booking; supplies current pickup values and tells us which
 *               customer details are missing
 *   loading   — disables the actions while a backend call is in flight
 *   onClose   — close without proceeding
 *   onConfirm — called with { pickupPersonName, pickupPersonMobile, customerEmail,
 *               customerName, changed }; the parent decides whether to PUT the booking
 *               and what to do next (loan-car handover or direct vehicle-received)
 */
const PickupConfirmModal = ({ booking, loading, onClose, onConfirm }) => {
    // Seed local draft from the booking. Empty strings are the unset state;
    // the parent will treat trimmed-blank as null on the wire.
    const [name, setName]     = useState(booking.pickupPersonName   ?? '');
    const [mobile, setMobile] = useState(booking.pickupPersonMobile ?? '');

    // Bookings taken by the AI phone bot may have no customer email — collecting one by
    // voice is unreliable, so it is captured here instead, with the customer at the
    // counter. The backend REFUSES the vehicle-received transition without it, so this
    // field is mandatory when shown. Customers who already have an email never see it.
    // What the phone bot could not capture. Both are mandatory when shown — the backend
    // refuses the transition without them — and invisible otherwise.
    const needsEmail = !(booking.customerEmail ?? '').trim();
    const needsName  = !(booking.customerName  ?? '').trim();

    const [email, setEmail] = useState('');
    const trimmedEmail = (email ?? '').trim();
    const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail);

    const [customerName, setCustomerName] = useState('');
    const trimmedCustomerName = (customerName ?? '').trim();
    // Any non-blank name is accepted. There is no shape a name must have, and inventing
    // one would reject exactly the callers this feature exists for — the workshop's
    // customers are named Nirmal, Abdul, Ali Shah and Yani Auto Pty Ltd.
    const customerNameValid = trimmedCustomerName !== '' && trimmedCustomerName.length <= 255;

    // Who the counter is talking about, for the prompts below. A nameless customer is
    // exactly the case here, so falling back to the phone number matters: without it the
    // email prompt reads "we don't have an email for " and trails off.
    const customerLabel = (booking.customerName ?? '').trim()
        || (booking.customerPhone ?? '').trim()
        || 'this customer';

    // Desktop-only Escape to close.
    useEscapeToClose(true, onClose);

    // Strip everything except digits, spaces, dashes, parens, plus — same
    // permissive shape used elsewhere; backend doesn't AU-format-check
    // pickup mobiles, so anything reasonable goes through.
    const handleMobileChange = (e) => {
        setMobile(e.target.value.replace(/[^\d\s\-()+]/g, ''));
    };

    // "Use customer details" quick-fill — common when the customer themselves
    // is collecting. Mirrors the same affordance on the booking form.
    const handleUseCustomer = () => {
        setName(booking.customerName ?? '');
        setMobile(booking.customerPhone ?? '');
    };

    const handleClear = () => {
        setName('');
        setMobile('');
    };

    // Compare trimmed values against the booking record so we can tell the
    // parent whether a PUT is needed.
    const trimmedName   = (name   ?? '').trim();
    const trimmedMobile = (mobile ?? '').trim();
    const currentName   = (booking.pickupPersonName   ?? '').trim();
    const currentMobile = (booking.pickupPersonMobile ?? '').trim();
    const changed = trimmedName !== currentName || trimmedMobile !== currentMobile;

    // Backend caps — match the @Size on the DTO (100 / 30).
    const tooLong = trimmedName.length > 100 || trimmedMobile.length > 30;

    const submit = () => {
        if (loading || tooLong) return;
        if (needsEmail && !emailValid) return;
        if (needsName && !customerNameValid) return;
        onConfirm({
            pickupPersonName:   trimmedName === '' ? null : trimmedName,
            pickupPersonMobile: trimmedMobile === '' ? null : trimmedMobile,
            // Only ever sent when it was missing. Receiving a vehicle must never be a way
            // to silently overwrite a customer who already has these.
            customerEmail:      needsEmail ? trimmedEmail : null,
            customerName:       needsName  ? trimmedCustomerName : null,
            changed,
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md flex flex-col shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                            <Car size={18} className="text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900">Confirm Pickup Person</h2>
                            <p className="text-xs text-gray-500">
                                Who will be collecting the vehicle after service?
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={loading}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">

                    {/* Helper banner — the pickup fields are optional, the email is not */}
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                        <Info size={13} className="text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800 leading-relaxed">
                            Pickup person fields are optional. Update if the pickup person
                            has changed since booking, or leave blank.
                        </p>
                    </div>

                    {/* Customer name — ONLY for a customer who has none, which means the AI
                        phone bot took this booking and never asked. The bot stopped asking
                        deliberately: across 28 real calls it captured 8 usable names and
                        stored "no", "please" and "car gel" as customers. The counter is where
                        a name is actually reliable, so this is where it gets collected. */}
                    {needsName && (
                        <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 space-y-2">
                            <div className="flex items-start gap-2">
                                <Info size={13} className="text-violet-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-violet-800 leading-relaxed">
                                    This booking was taken by phone on{' '}
                                    <span className="font-semibold">{customerLabel}</span> and we
                                    don't have a name for it yet. Please ask and enter it now.
                                </p>
                            </div>
                            <div>
                                <label htmlFor="pickup-confirm-customer-name"
                                       className="block text-xs font-medium text-gray-700 mb-1">
                                    Customer name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <User size={14}
                                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        id="pickup-confirm-customer-name"
                                        type="text"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        placeholder="e.g. Jane Smith"
                                        maxLength={255}
                                        disabled={loading}
                                        autoFocus
                                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 disabled:bg-gray-50"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Customer email — ONLY for a customer who has none, which in practice
                        means the booking came from the AI phone bot. Mandatory: the backend
                        refuses the transition without it, so blocking here gives staff a
                        field to fill instead of an error with no way forward. */}
                    {needsEmail && (
                        <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 space-y-2">
                            <div className="flex items-start gap-2">
                                <Info size={13} className="text-violet-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-violet-800 leading-relaxed">
                                    This booking was taken by phone and we don't have an email
                                    for <span className="font-semibold">{customerLabel}</span>.
                                    Please collect one now — it's needed for the invoice and
                                    completion report.
                                </p>
                            </div>
                            <div>
                                <label htmlFor="pickup-confirm-email"
                                       className="block text-xs font-medium text-gray-700 mb-1">
                                    Customer email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Mail size={14}
                                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        id="pickup-confirm-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="e.g. jane@example.com"
                                        maxLength={255}
                                        disabled={loading}
                                        {...(needsName ? {} : { autoFocus: true })}
                                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 disabled:bg-gray-50"
                                    />
                                </div>
                                {trimmedEmail !== '' && !emailValid && (
                                    <p className="text-xs text-red-600 mt-1">
                                        Please enter a valid email address.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Quick actions */}
                    <div className="flex items-center justify-between gap-2">
                        <button type="button" onClick={handleUseCustomer} disabled={loading}
                                className="text-xs text-violet-600 hover:text-violet-700 font-medium disabled:opacity-50">
                            Use customer details
                        </button>
                        {(name || mobile) && (
                            <button type="button" onClick={handleClear} disabled={loading}
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50">
                                Clear
                            </button>
                        )}
                    </div>

                    {/* Name */}
                    <div>
                        <label htmlFor="pickup-confirm-name"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Pickup person name
                        </label>
                        <div className="relative">
                            <User size={14}
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                id="pickup-confirm-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Jane Smith"
                                maxLength={100}
                                disabled={loading}
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 disabled:bg-gray-50"
                            />
                        </div>
                    </div>

                    {/* Mobile */}
                    <div>
                        <label htmlFor="pickup-confirm-mobile"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Pickup mobile
                        </label>
                        <div className="relative">
                            <Phone size={14}
                                   className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                id="pickup-confirm-mobile"
                                type="tel"
                                value={mobile}
                                onChange={handleMobileChange}
                                placeholder="e.g. 0412 345 678"
                                maxLength={30}
                                disabled={loading}
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 disabled:bg-gray-50"
                            />
                        </div>
                    </div>

                    {tooLong && (
                        <p className="text-xs text-red-600 flex items-center gap-1.5">
                            <AlertCircle size={12} />
                            Name must be ≤ 100 characters and mobile ≤ 30 characters.
                        </p>
                    )}

                    {/* Status — tells the user whether their next click will save */}
                    <p className="text-xs text-gray-500">
                        {changed
                            ? 'Pickup person will be saved before the vehicle is marked received.'
                            : 'No changes — vehicle will be marked received with the recorded pickup person.'}
                    </p>
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-5 pt-0">
                    <button type="button" onClick={onClose} disabled={loading}
                            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={submit}
                            disabled={loading || tooLong || (needsEmail && !emailValid) || (needsName && !customerNameValid)}
                            className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        {changed ? 'Save & Receive Vehicle' : 'Confirm & Receive Vehicle'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PickupConfirmModal;
